import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProducts, deleteProduct, getCategories } from "../api/menupro.api";
import { getBusiness, logout } from "../store/menupro.store";
import "./dashboard.css";

export default function Dashboard() {
    const [products, setProducts] = useState([]);
    const [cats, setCats] = useState([]);
    const [q, setQ] = useState("");
    const [activeCat, setActiveCat] = useState("ALL");
    const [copied, setCopied] = useState(false);

    const business = getBusiness();
    const navigate = useNavigate();

    const catWrapRef = useRef(null);
    const catBtnRefs = useRef({});

    // ✅ HOOK həmişə çağırılır (conditional deyil)
    const filtered = useMemo(() => {
        if (!business) return []; // business yoxdursa boş qaytar

        const text = q.trim().toLowerCase();
        return (products || [])
            .filter((p) => {
                if (activeCat === "ALL") return true;
                return String(p.categoryId) === String(activeCat);
            })
            .filter((p) => {
                if (!text) return true;
                const t = (p.title || "").toLowerCase();
                const d = (p.description || "").toLowerCase();
                return t.includes(text) || d.includes(text);
            });
    }, [business, products, q, activeCat]);

    useEffect(() => {
        if (!business) {
            navigate("/menupro/login");
            return;
        }
        loadAll();
        // eslint-disable-next-line
    }, []);

    const loadAll = async () => {
        try {
            const [pRes, cRes] = await Promise.all([
                getProducts(business.id),
                getCategories(business.id),
            ]);
            setProducts(pRes.data || []);
            setCats(cRes.data || []);
        } catch (e) {
            console.log(e);
        }
    };

    if (!business) {
        return (
            <div className="dash-container" style={{ padding: 30 }}>
                <div style={{ background: "#fff", borderRadius: 16, padding: 20 }}>
                    Yüklənir...
                </div>
            </div>
        );
    }

    const menuUrl = `https://mototaksi.az/${business.slug}`;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(menuUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch {
            alert("Kopyalama alınmadı");
        }
    };

    const handleDelete = async (id) => {
        const ok = window.confirm("Bu məhsulu silmək istəyirsən?");
        if (!ok) return;

        try {
            await deleteProduct(id);
            setProducts((prev) => prev.filter((x) => x.id !== id));
        } catch {
            alert("Silinmədi, xəta baş verdi");
        }
    };

    const scrollCatToCenter = (key) => {
        const wrap = catWrapRef.current;
        const btn = catBtnRefs.current[key];
        if (!wrap || !btn) return;

        const wrapRect = wrap.getBoundingClientRect();
        const btnRect = btn.getBoundingClientRect();
        const currentScroll = wrap.scrollLeft;

        const btnCenter = btnRect.left + btnRect.width / 2;
        const wrapCenter = wrapRect.left + wrapRect.width / 2;

        const delta = btnCenter - wrapCenter;
        wrap.scrollTo({ left: currentScroll + delta, behavior: "smooth" });
    };

    const onSelectCat = (key) => {
        setActiveCat(key);
        scrollCatToCenter(key);
    };

    const activeCatTitle =
        activeCat === "ALL"
            ? "Hamısı"
            : (cats.find((x) => String(x.id) === String(activeCat))?.name || "Kateqoriya");

    return (
        <div className="dash-container">
            {/* TOP CARD */}
            <div className="dash-topcard">
                <div className="profile">
                    <img
                        src={business.profileImage || "https://img.icons8.com/fluent/1200/shop.jpg"}
                        alt=""
                    />

                    <div className="profile-meta">
                        <h2>{business.businessName}</h2>
                        <p>{business.bio || "Bio əlavə edilməyib"}</p>

                        <div className="menu-link-row">
                            <span className="menu-label">Menyu linki:</span>
                            <a className="menu-link" href={menuUrl} target="_blank" rel="noreferrer">
                                {menuUrl}
                            </a>

                            <button className="copy-btn" onClick={handleCopy}>
                                {copied ? "Kopyalandı ✓" : "Kopyala"}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="actions">
                    <button className="btn ghost" onClick={() => navigate("/menupro/profile")}>
                        Profili düzəlt
                    </button>
                    <button className="btn primary" onClick={() => navigate("/menupro/add-product")}>
                        Məhsul əlavə et
                    </button>
                    <button
                        className="btn dark"
                        onClick={() => {
                            logout();
                            navigate("/menupro/login");
                        }}
                    >
                        Çıxış
                    </button>
                </div>
            </div>

            {/* TOOLBAR */}
            <div className="dash-toolbar">
                <div className="search-wrap">
                    <input
                        className="search"
                        placeholder="Məhsul axtar... (məs: burger, kola)"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    {q && (
                        <button className="clear" onClick={() => setQ("")} title="Təmizlə">
                            ×
                        </button>
                    )}
                </div>

                <div className="count">
                    {filtered.length} məhsul
                </div>
            </div>

            {/* CATEGORY CHIPS */}
            <div className="cat-wrap" ref={catWrapRef}>
                <button
                    ref={(el) => (catBtnRefs.current["ALL"] = el)}
                    className={`cat-chip ${activeCat === "ALL" ? "active" : ""}`}
                    onClick={() => onSelectCat("ALL")}
                >
                    Hamısı
                </button>

                {cats.map((c) => (
                    <button
                        key={c.id}
                        ref={(el) => (catBtnRefs.current[String(c.id)] = el)}
                        className={`cat-chip ${String(activeCat) === String(c.id) ? "active" : ""}`}
                        onClick={() => onSelectCat(String(c.id))}
                    >
                        {c.name}
                    </button>
                ))}
            </div>

            <div className="products-head">
                <div className="products-title">{activeCatTitle}</div>
            </div>

            {/* PRODUCTS */}
            <div className="product-list">
                {filtered.map((p) => (
                    <div key={p.id} className="product-rowcard">
                        <div className="pr-left">
                            <div className="pr-title">{p.title}</div>

                            {p.description && (
                                <div className="pr-desc">{p.description}</div>
                            )}

                            <div className="pr-bottom">
                                <span className="pr-price">{p.price} ₼</span>

                                <div className="pr-actions">
                                    <button
                                        className="pr-btn edit"
                                        onClick={() => navigate(`/menupro/edit-product/${p.id}`)}
                                    >
                                        Düzəlt
                                    </button>
                                    <button className="pr-btn del" onClick={() => handleDelete(p.id)}>
                                        Sil
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="pr-right">
                            <img
                                className="pr-img"
                                src={p.image || "https://img.icons8.com/fluent/1200/shop.jpg"}
                                alt=""
                                loading="lazy"
                            />
                        </div>
                    </div>
                ))}

                {filtered.length === 0 && (
                    <div className="empty">Heç nə tapılmadı.</div>
                )}
            </div>
        </div>
    );
}

